/**
 * ItemService - SetItem関連の操作を管理
 */

import type { AppState, Image, SetItem, GraphOperation } from '../types'
import { FractionalIndex } from '../utils/FractionalIndex'
import type { ImageStore } from '../ImageStore'

export class ItemService {
  private imageStore: ImageStore

  constructor(imageStore: ImageStore) {
    this.imageStore = imageStore
  }

  /**
   * 画像を追加してSetItemを作成
   */
  async addImageToSet(
    state: AppState,
    setId: string, 
    imageData: string, 
    position?: number
  ): Promise<{ image: Image; item: SetItem; operations: GraphOperation[] }> {
    // IndexedDBに画像を保存
    const imageId = await this.imageStore.saveImage(imageData)
    
    // Image エンティティを作成
    const image: Image = {
      id: imageId,
      metadata: undefined // メタデータは後で設定
    }
    
    // Blob URLを生成（表示用）
    const blob = await this.imageStore.getImageBlob(imageId)
    if (blob) {
      image.url = URL.createObjectURL(blob)
    }
    
    // サムネイルURLも生成
    const thumbnailBlob = await this.imageStore.getThumbnailBlob(imageId)
    if (thumbnailBlob) {
      image.thumbnailUrl = URL.createObjectURL(thumbnailBlob)
    }
    
    // SetItem エンティティを作成
    const setItems = Object.values(state.setItems).filter(item => item.setId === setId)
    const order = FractionalIndex.getOrderForPosition(setItems, position ?? setItems.length)
    
    const item: SetItem = {
      id: crypto.randomUUID(),
      setId,
      imageId,
      order,
      metadata: {
        capturedAt: new Date()
      }
    }
    
    // 操作を生成
    const operations: GraphOperation[] = [
      { type: 'ADD_IMAGE', payload: image },
      { type: 'ADD_ITEM', payload: item }
    ]
    
    return { image, item, operations }
  }

  /**
   * SetItemの順序を変更
   */
  static moveItem(state: AppState, itemId: string, newPosition: number): GraphOperation | null {
    const item = state.setItems[itemId]
    if (!item) return null
    
    const setItems = Object.values(state.setItems)
      .filter(i => i.setId === item.setId)
      .sort((a, b) => a.order - b.order)
    
    const currentIndex = setItems.findIndex(i => i.id === itemId)
    
    // 現在位置より後ろに移動する場合は、自分を除外した後のインデックスを1つ減らす
    const adjustedPosition = currentIndex < newPosition ? newPosition - 1 : newPosition
    
    const newOrder = FractionalIndex.getOrderForPosition(
      setItems.filter(i => i.id !== itemId),
      adjustedPosition
    )
    
    return {
      type: 'UPDATE_ITEM_ORDER',
      payload: { itemId, newOrder }
    }
  }

  /**
   * 画像とSetItemを削除
   */
  async *deleteImage(state: AppState, imageId: string): AsyncGenerator<GraphOperation> {
    // IndexedDBから削除
    await this.imageStore.deleteImage(imageId)
    
    // 関連するSetItemsを削除
    for (const item of Object.values(state.setItems)) {
      if (item.imageId === imageId) {
        yield { type: 'DELETE_ITEM', payload: { itemId: item.id } }
      }
    }
    
    // 関連するNodesを削除
    for (const node of Object.values(state.nodes)) {
      if (node.imageId === imageId) {
        // このノードに関連するConnectionsも削除
        for (const conn of Object.values(state.connections)) {
          if (conn.fromNodeId === node.id || conn.toNodeId === node.id) {
            yield { type: 'DELETE_CONNECTION', payload: { connectionId: conn.id } }
          }
        }
        yield { type: 'DELETE_NODE', payload: { nodeId: node.id } }
      }
    }
    
    // Image自体を削除
    yield { type: 'DELETE_IMAGE', payload: { imageId } }
  }

  /**
   * 画像を取得（Base64形式）
   */
  async getImage(imageId: string): Promise<string | null> {
    return this.imageStore.getImage(imageId)
  }

  /**
   * サムネイルを取得
   */
  async getThumbnail(imageId: string): Promise<string | null> {
    return this.imageStore.getThumbnail(imageId)
  }
}